import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@/components/ui/Dialog';
import { useDriveSubFolders, useDriveFiles } from '@/hooks/useDocFlow';
import type { IDriveFile } from '@/types/docflow';
import { ChevronRight } from 'lucide-react';

type Child = 'Joshua' | 'Amelie' | 'Sherilyn';
type ImageSource = 'drive' | 'device';

const MISTAKE_NOTEBOOK_ROOT = '1vmho6uRYPTtWVTKlARIGehvi-eKey8iq';

const ELEMENTARY_SUBJECTS = ['國語文', '英語文', '數學', '社會', '自然科學', '藝術', '綜合活動', '健康與體育'];
const JUNIOR_HIGH_SUBJECTS = ['國文', '英語', '數學', '歷史', '地理', '公民', '生物', '理化', '地科', '科技', '綜合活動', '藝術', '健康與體育'];

function subjectsForGrade(grade: number): string[] {
  if (grade >= 1 && grade <= 6) return ELEMENTARY_SUBJECTS;
  if (grade >= 7 && grade <= 9) return JUNIOR_HIGH_SUBJECTS;
  return [];
}

interface UploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  activeChild: Child;
}

// ── Inline Drive Browser (reusable from PartCreateFilePicker pattern) ──

const InlineDriveBrowser: React.FC<{
  rootFolderId: string;
  rootLabel: string;
  onSelectFile: (file: IDriveFile) => void;
  selectedFileId?: string;
}> = ({ rootFolderId, rootLabel, onSelectFile, selectedFileId }) => {
  const [currentFolderId, setCurrentFolderId] = useState(rootFolderId);
  const [breadcrumbs, setBreadcrumbs] = useState<{ id: string; name: string }[]>([
    { id: rootFolderId, name: rootLabel },
  ]);

  const { data: folders, isLoading: foldersLoading } = useDriveSubFolders(currentFolderId);
  const { data: files, isLoading: filesLoading } = useDriveFiles(currentFolderId);

  const navigateToFolder = useCallback((folderId: string, folderName: string) => {
    setCurrentFolderId(folderId);
    setBreadcrumbs(prev => [...prev, { id: folderId, name: folderName }]);
  }, []);

  const navigateToBreadcrumb = useCallback((index: number) => {
    const crumb = breadcrumbs[index];
    setCurrentFolderId(crumb.id);
    setBreadcrumbs(prev => prev.slice(0, index + 1));
  }, [breadcrumbs]);

  const isLoading = foldersLoading || filesLoading;

  const imageFiles = (files || []).filter(f => {
    const mime = f.mime_type || '';
    const name = f.name.toLowerCase();
    return mime.startsWith('image/') || name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.webp');
  });

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-1 px-3 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 text-xs overflow-x-auto">
        {breadcrumbs.map((crumb, i) => (
          <span key={crumb.id} className="flex items-center gap-1 whitespace-nowrap">
            {i > 0 && <ChevronRight className="w-3 h-3 text-gray-400" />}
            <button
              onClick={() => navigateToBreadcrumb(i)}
              className={`hover:text-pink-600 dark:hover:text-pink-400 ${
                i === breadcrumbs.length - 1
                  ? 'text-pink-600 dark:text-pink-400 font-medium'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {crumb.name}
            </button>
          </span>
        ))}
      </div>

      {/* Content */}
      <div className="max-h-48 overflow-y-auto">
        {isLoading ? (
          <div className="text-sm text-gray-400 text-center py-6">Loading...</div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {(folders || []).map(folder => (
              <button
                key={folder.id}
                onClick={() => navigateToFolder(folder.id, folder.name)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <svg className="w-4 h-4 text-yellow-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <span className="truncate text-gray-700 dark:text-gray-200">{folder.name}</span>
                <ChevronRight className="w-3 h-3 text-gray-400 ml-auto shrink-0" />
              </button>
            ))}

            {imageFiles.map(file => {
              const isSelected = selectedFileId === file.id;
              return (
                <button
                  key={file.id}
                  onClick={() => onSelectFile(file)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
                    isSelected
                      ? 'bg-pink-50 dark:bg-pink-900/30'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <svg className={`w-4 h-4 shrink-0 ${isSelected ? 'text-pink-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className={`truncate ${isSelected ? 'text-pink-700 dark:text-pink-300 font-medium' : 'text-gray-700 dark:text-gray-200'}`}>
                    {file.name}
                  </span>
                  {file.size !== undefined && (
                    <span className="text-xs text-gray-400 ml-auto shrink-0">
                      {formatFileSize(file.size)}
                    </span>
                  )}
                </button>
              );
            })}

            {(folders || []).length === 0 && imageFiles.length === 0 && (
              <div className="text-sm text-gray-400 text-center py-6">
                No image files in this folder
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

// ── Upload Dialog ──

export default function UploadDialog({ isOpen, onClose, activeChild }: UploadDialogProps) {
  const { t } = useTranslation('mistakeNotebook');
  const [child, setChild] = useState<Child>(activeChild);
  const [grade, setGrade] = useState<number>(0);
  const [subject, setSubject] = useState('');
  const [imageSource, setImageSource] = useState<ImageSource>('drive');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [driveFile, setDriveFile] = useState<IDriveFile | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const subjects = grade > 0 ? subjectsForGrade(grade) : [];

  const handleGradeChange = (g: number) => {
    setGrade(g);
    setSubject('');
  };

  const handleSourceChange = (source: ImageSource) => {
    setImageSource(source);
    setImagePreview(null);
    setImageFile(null);
    setDriveFile(null);
  };

  const handleDriveSelect = (file: IDriveFile) => {
    setDriveFile(file);
    setImagePreview(null);
    setImageFile(null);
  };

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      loadLocalImage(file);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) loadLocalImage(file);
  };

  const loadLocalImage = (file: File) => {
    setImageFile(file);
    setDriveFile(null);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!grade || !subject) return;
    if (imageSource === 'device' && !imageFile) return;
    if (imageSource === 'drive' && !driveFile) return;

    setSubmitting(true);
    try {
      if (imageSource === 'device' && imageFile) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const base64 = (e.target?.result as string).split(',')[1];
          await submitUpload(base64, imageFile.type);
        };
        reader.readAsDataURL(imageFile);
      } else if (imageSource === 'drive' && driveFile) {
        // For Drive files, send the file ID and let the backend fetch the content
        await submitUpload('', '', driveFile.id);
      }
    } catch {
      alert(t('upload.error'));
      setSubmitting(false);
    }
  };

  const submitUpload = async (base64: string, mimeType: string, driveFileId?: string) => {
    try {
      const resp = await fetch(`/api/v1/mistake-notebook/${child}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          grade,
          subject,
          image_data: base64,
          mime_type: mimeType || 'image/png',
          drive_file_id: driveFileId || '',
          frame_data: { image_width: 0, image_height: 0, frames: [] },
        }),
      });

      if (!resp.ok) throw new Error(`Upload failed: ${resp.status}`);

      onClose();
      resetForm();
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setGrade(0);
    setSubject('');
    setImagePreview(null);
    setImageFile(null);
    setDriveFile(null);
  };

  const hasImage = (imageSource === 'device' && imageFile) || (imageSource === 'drive' && driveFile);
  const canSubmit = hasImage && grade > 0 && subject !== '' && !submitting;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('upload.title')}
      headerColor="pink"
      maxWidth="lg"
      icon={
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      }
      footer={
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            {t('actions.cancel', { ns: 'common', defaultValue: 'Cancel' })}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-pink-600 rounded-lg hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? t('upload.submitting') : t('upload.submit')}
          </button>
        </div>
      }
    >
      <div className="space-y-5 p-1">
        {/* Child Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('upload.selectChild')}
          </label>
          <div className="flex gap-2">
            {(['Joshua', 'Amelie', 'Sherilyn'] as Child[]).map((c) => (
              <button
                key={c}
                onClick={() => setChild(c)}
                className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
                  child === c
                    ? 'bg-pink-50 border-pink-300 text-pink-700 dark:bg-pink-900/30 dark:border-pink-600 dark:text-pink-300'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
                }`}
              >
                {t(`children.${c.toLowerCase()}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Grade Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('upload.selectGrade')}
          </label>
          <div className="grid grid-cols-9 gap-1.5">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((g) => (
              <button
                key={g}
                onClick={() => handleGradeChange(g)}
                className={`py-2 text-sm rounded-lg border transition-colors ${
                  grade === g
                    ? 'bg-pink-50 border-pink-300 text-pink-700 dark:bg-pink-900/30 dark:border-pink-600 dark:text-pink-300'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
                }`}
              >
                {g}
              </button>
            ))}
          </div>
          {grade > 0 && (
            <p className="text-xs text-gray-400 mt-1">
              {grade <= 6 ? t('subjects.elementary') : t('subjects.juniorHigh')}
            </p>
          )}
        </div>

        {/* Subject Selector */}
        {grade > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('upload.selectSubject')}
            </label>
            <div className="flex flex-wrap gap-2">
              {subjects.map((s) => (
                <button
                  key={s}
                  onClick={() => setSubject(s)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                    subject === s
                      ? 'bg-pink-50 border-pink-300 text-pink-700 dark:bg-pink-900/30 dark:border-pink-600 dark:text-pink-300'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Image Source Toggle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('upload.selectImage')}
          </label>
          <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600 mb-3">
            <button
              onClick={() => handleSourceChange('drive')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm ${
                imageSource === 'drive'
                  ? 'bg-pink-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.71 3.5L1.15 15l3.43 5.97L11.14 9.47 7.71 3.5zm1.14 0h6.86l6.58 11.5h-6.86L8.85 3.5zM19.57 15H5.71l-3.43 6h13.86l3.43-6z" />
              </svg>
              Google Drive
            </button>
            <button
              onClick={() => handleSourceChange('device')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm border-l border-gray-300 dark:border-gray-600 ${
                imageSource === 'device'
                  ? 'bg-pink-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              {t('upload.deviceUpload', { defaultValue: 'Device Upload' })}
            </button>
          </div>

          {/* Drive Browser */}
          {imageSource === 'drive' && (
            <>
              <InlineDriveBrowser
                rootFolderId={MISTAKE_NOTEBOOK_ROOT}
                rootLabel="Mistake Notebook"
                onSelectFile={handleDriveSelect}
                selectedFileId={driveFile?.id}
              />
              {driveFile && (
                <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded-lg">
                  <svg className="w-4 h-4 text-pink-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm text-pink-700 dark:text-pink-300 truncate">{driveFile.name}</span>
                </div>
              )}
            </>
          )}

          {/* Device Upload */}
          {imageSource === 'device' && (
            <>
              {imagePreview ? (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full max-h-48 object-contain rounded-lg border border-gray-200 dark:border-gray-600"
                  />
                  <button
                    onClick={() => { setImagePreview(null); setImageFile(null); }}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleFileDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-pink-400 dark:hover:border-pink-500 transition-colors"
                >
                  <svg className="w-10 h-10 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('upload.dropImage')}</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}
